import React, { useEffect } from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';

import classNames from 'classnames';
import debounce from 'lodash/debounce';

import { setFieldValue } from 'actions';
import { MoneyInput } from 'components/FieldList/Field/MoneyInputField/MoneyInput';
import { FormattedAmount } from 'components/FormattedAmount/FormattedAmount';
import type { PaymentItem } from 'models/item';
import type { RootState } from 'reducers/types';
import {
  getCurrentCurrency,
  getDefaultAmountForPayers,
  getItems,
  getTotalAmount,
} from 'selectors';
import { useTranslations } from 'utils';

import './PaymentItems.scss';

const WAIT_TIME = 250;

type PaymentItemsProps = ConnectedProps<typeof connector> & {
  hideTotalAmount?: boolean;
  error?: string;
  hint?: React.ReactNode;
  onBlur?: (name: string, value: number) => void;
  onChange?: (name: string, value: number) => void;
};

export const PaymentItemsComponent = ({
  currency,
  items,
  totalAmount,
  recipientDefaultAmount,
  hideTotalAmount = false,
  error,
  hint,
  onBlur,
  onChange,
  setFieldValue,
}: PaymentItemsProps) => {
  const i18n = useTranslations();

  // biome-ignore lint/correctness/useExhaustiveDependencies: items[0]
  useEffect(() => {
    const firstItem = items[0];
    const firstItemValue = firstItem?.defaultValue || recipientDefaultAmount;

    if (recipientDefaultAmount && firstItemValue) {
      setFieldValue?.(firstItem.id, firstItemValue);
    }
  }, []);

  const getItemDefaultValue = (itemIndex: number, item: PaymentItem) => {
    const { defaultValue: itemDefaultValue } = item;

    if (itemIndex > 0) {
      return itemDefaultValue;
    }

    const defaultValue = itemDefaultValue || recipientDefaultAmount;
    return defaultValue && defaultValue.toString();
  };

  const commonProps = ({ id, readOnly, apiReadOnly, label }: PaymentItem) => ({
    currencySymbol: currency.symbol,
    decimalMark: currency.decimalMark,
    label: label,
    maxLength: 12,
    name: id,
    readOnly: readOnly || apiReadOnly,
    subunitToUnit: currency.subunitToUnit,
    symbolFirst: currency.symbolFirst,
    thousandsSeparator: currency.thousandsSeparator,
    onBlur: onBlur,
    onChange: onChange ? debounce(onChange, WAIT_TIME) : undefined,
  });

  if (items.length === 1) {
    const singlePaymentItem = items.map((item, index) => (
      <MoneyInput
        key={item.id}
        {...commonProps(item)}
        error={error}
        hint={hint ? (hint as string) : undefined}
        aria-describedby="paymentInformation-amount-msg"
        value={getItemDefaultValue(index, item) as number}
        required
      />
    ));
    return <div>{singlePaymentItem}</div>;
  }

  return (
    <div className="PaymentItems">
      <p className="PaymentItems-hint">{i18n.t('paymentItems.hint')}</p>
      <div className="flex flex-col space-y-8">
        {items.map((item, index) => {
          return (
            <MoneyInput
              aria-describedby="paymentInformation-amount-msg paymentItems-error-msg paymentItems-hint-msg"
              key={item.id}
              {...commonProps(item)}
              error={error ? '' : undefined}
              value={getItemDefaultValue(index, item) as number}
              required
            />
          );
        })}
      </div>
      {!hideTotalAmount && (
        <div
          className={classNames('PaymentItems-total mt-8', {
            'has-error': error,
          })}
          aria-labelledby="paymentItems-hint-msg"
          aria-describedby="paymentItems-error-msg"
          role="status"
        >
          <span className="PaymentItems-totalTitle">
            {i18n.t('paymentItems.total')}
          </span>
          <span className="PaymentItems-totalAmount">
            <FormattedAmount value={totalAmount} currency={currency.code} />
          </span>
        </div>
      )}
      <p
        className="PaymentItems-error"
        id="paymentItems-error-msg"
        aria-live="off"
      >
        {error && typeof error === 'string' ? error : null}
      </p>
      {!error && (
        <p className="FormGroup-hint" id="paymentItems-hint-msg">
          {hint}
        </p>
      )}
    </div>
  );
};

const mapDispatchToProps = {
  setFieldValue,
};

const mapStateToProps = (state: RootState) => ({
  currency: getCurrentCurrency(state),
  items: getItems(state),
  totalAmount: getTotalAmount(state),
  recipientDefaultAmount: getDefaultAmountForPayers(state),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
const PaymentItems = connector(PaymentItemsComponent);

export { PaymentItems };
